import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import { Drawer } from 'vaul'
import PrimarySidebar from './PrimarySidebar'
import { useAppShellStore } from '../../stores/appShellStore'

const L2_HOST_ID = 'mobile-l2-host'

// Single persistent left drawer, mounted ONCE at App root (NOT per-page in
// AppLayout). It holds the L1 module rail + an empty L2 "host" slot. The
// active route's L2 sidebar (channel list / mail folders / …) is portaled
// into that host by <MobileSecondaryPortal> below — so the route's LIVE
// sidebar (current props/handlers, no stale snapshot) renders here while the
// drawer shell itself never remounts. That keeps it from re-animating open on
// every section switch ("переоткрывается") WITHOUT the content going
// stale/empty (the bug that snapshotting the node into a store caused).
//
// Auto-closes on navigation: tapping an L2 item (channel / DM / folder) — or
// an L1 module — navigates, and the drawer slides out to reveal the content
// the user just picked. Because the drawer is persistent (App-level), this is
// a clean close, not the old remount flicker. It also closes on a backdrop tap
// (vaul onOpenChange) or the AI-Assistant button. z-[100] keeps it ABOVE the
// ActiveCallBar (z-[90]) so the open menu covers the call bar.
export default function MobileNavDrawer() {
  const open = useAppShellStore((s) => s.mobileNavOpen)
  const setOpen = useAppShellStore((s) => s.setMobileNavOpen)
  const location = useLocation()

  // Close whenever the route changes (the navigation a tapped L2/L1 item
  // triggers). setOpen is a stable zustand setter, so pathname is the only dep.
  useEffect(() => {
    setOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <Drawer.Root open={open} onOpenChange={setOpen} direction="left">
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-[100] bg-black/50" />
        <Drawer.Content
          // bg-card on the Content itself (not only the inner div) so the
          // safe-area padding strip is card-colored, not a transparent gap
          // showing the overlay (visible on Android where the top inset > 0).
          className="fixed inset-y-0 left-0 z-[100] flex w-[320px] max-w-[88%] outline-none bg-card"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <Drawer.Title className="sr-only">Navigation</Drawer.Title>
          <div className="flex h-full w-full bg-card">
            {/* L1 — module rail (reused). onNavigate only fires for the AI
                button (hands off to the AI sheet); module/section taps keep
                the drawer open per the no-auto-close note above. */}
            <PrimarySidebar onNavigate={() => setOpen(false)} />
            {/* L2 — host slot. The active route's AppLayout portals its
                secondary sidebar in here; empty for routes with no secondary
                (e.g. Calendar). */}
            <div id={L2_HOST_ID} className="flex-1 min-w-0 overflow-hidden bg-card" />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}

// Portals a route's L2 secondary sidebar into the persistent drawer's host.
// Mounted by each route's AppLayout (mobile only). Renders nothing until the
// drawer is open — the host only exists in the DOM while vaul has the drawer
// content mounted, so we re-resolve it whenever `open` flips. React commits
// the whole tree (incl. the host) before passive effects run, so the
// getElementById here reliably finds the host on the open frame.
export function MobileSecondaryPortal({ children }: { children: ReactNode }) {
  const open = useAppShellStore((s) => s.mobileNavOpen)
  const [host, setHost] = useState<HTMLElement | null>(null)
  // vaul mounts the drawer content (and our L2 host) a frame or two AFTER
  // `open` flips — so a single getElementById on the open-tick finds nothing
  // and the L2 stays empty. Poll on rAF until the host appears (capped so a
  // genuinely-missing host can't spin forever). When closed, drop the host so
  // the portal unmounts. Resolves instantly when navigating with the drawer
  // already open (host already present in the persistent drawer).
  useEffect(() => {
    if (!open) {
      setHost(null)
      return
    }
    let raf = 0
    let tries = 0
    const find = () => {
      const h = document.getElementById(L2_HOST_ID)
      if (h) {
        setHost(h)
      } else if (tries++ < 60) {
        raf = requestAnimationFrame(find)
      }
    }
    raf = requestAnimationFrame(find)
    return () => cancelAnimationFrame(raf)
  }, [open])
  return host ? createPortal(children, host) : null
}
